/**
 * LoggingPanel
 *
 * Read-only, admin-style overview of job and step-run history for ANY configured
 * customer — independent of which customer (if any) is selected elsewhere on the
 * page. Never calls connect/disconnect; only lists customers and reads history.
 *
 * Note: job/step history is served by each customer's own ETL service container
 * via the /etl-api/{customerId}/* proxy, which requires that customer to be
 * "active" (connected) — history for a disconnected customer isn't reachable
 * without starting its container. Docker container logs, which would work
 * regardless of connection state, are out of scope for now.
 */

import { useEffect, useMemo, useState } from 'react';
import { createCustomerETLApi, getCustomers } from '../api/etlServiceApi';
import { STEP_DEFS } from './StepDebugPanel';
import ETLJobHistory from './ETLJobHistory';

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    connecting: 'bg-blue-100 text-blue-700',
    error: 'bg-red-100 text-red-600',
    inactive: 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${map[status] ?? map.inactive}`}>
      {status ?? 'inactive'}
    </span>
  );
}

function LogLines({ lines }) {
  if (!lines?.length) {
    return <p className="text-[11px] text-gray-500 italic">No logs recorded for this run.</p>;
  }
  return (
    <div className="bg-gray-950 rounded-lg p-2.5 font-mono text-[11px] leading-5 max-h-64 overflow-y-auto">
      {lines.map((line, i) => {
        const isError = line.includes('ERROR');
        const isWarning = !isError && (line.includes('WARNING') || line.includes('WARN'));
        const cls = isError ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-gray-300';
        return (
          <div key={i} className={`py-0.5 whitespace-pre-wrap break-all ${cls}`}>
            {line}
          </div>
        );
      })}
    </div>
  );
}

function StepRunRow({ run }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-b border-gray-50 last:border-b-0">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-2 py-2 text-left hover:bg-gray-50 px-1 rounded transition-colors"
      >
        <svg
          className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
        </svg>
        <span className="text-xs font-mono text-gray-600 flex-1 truncate">
          {run.started_at ? new Date(run.started_at).toLocaleString() : run.step_run_id}
        </span>
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
          run.status === 'done' ? 'bg-emerald-50 text-emerald-700'
          : run.status === 'failed' ? 'bg-red-50 text-red-600'
          : run.status === 'running' ? 'bg-blue-50 text-blue-700'
          : 'bg-gray-100 text-gray-500'
        }`}>
          {run.status}
        </span>
        <span className="text-[11px] text-gray-400 flex-shrink-0">{run.logs?.length ?? 0} lines</span>
      </button>
      {expanded && (
        <div className="pb-2 px-1">
          {run.error && <p className="text-[11px] text-red-500 mb-1.5">{run.error}</p>}
          <LogLines lines={run.logs} />
        </div>
      )}
    </div>
  );
}

export default function LoggingPanel() {
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customerId, setCustomerId] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState(null);

  const [selectedStepId, setSelectedStepId] = useState(STEP_DEFS[0].id);
  const [stepHistory, setStepHistory] = useState([]);
  const [stepHistoryLoading, setStepHistoryLoading] = useState(false);
  const [stepHistoryError, setStepHistoryError] = useState(null);

  useEffect(() => {
    getCustomers()
      .then(data => setCustomers(data.customers ?? []))
      .catch(() => setCustomers([]))
      .finally(() => setCustomersLoading(false));
  }, []);

  const selectedCustomer = customers.find(c => c.customer_id === customerId) ?? null;
  const isCustomerActive = selectedCustomer?.status === 'active';
  const api = useMemo(
    () => (customerId && isCustomerActive ? createCustomerETLApi(customerId) : null),
    [customerId, isCustomerActive],
  );

  useEffect(() => {
    if (!api) { setJobs([]); return; }
    setJobsLoading(true);
    setJobsError(null);
    api.getJobs(50)
      .then(setJobs)
      .catch(err => setJobsError(err?.response?.data?.detail ?? err.message ?? 'Failed to load jobs'))
      .finally(() => setJobsLoading(false));
  }, [api]);

  useEffect(() => {
    if (!api) { setStepHistory([]); return; }
    setStepHistoryLoading(true);
    setStepHistoryError(null);
    api.getStepHistory(selectedStepId, 20)
      .then(setStepHistory)
      .catch(err => setStepHistoryError(err?.response?.data?.detail ?? err.message ?? 'Failed to load step history'))
      .finally(() => setStepHistoryLoading(false));
  }, [api, selectedStepId]);

  return (
    <div className="space-y-4">
      {/* Customer picker */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Customer</h3>
        {customersLoading ? (
          <p className="text-xs text-gray-400">Loading customers…</p>
        ) : customers.length === 0 ? (
          <p className="text-xs text-gray-400">No customers configured.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {customers.map(c => (
              <button
                key={c.customer_id}
                onClick={() => setCustomerId(c.customer_id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  customerId === c.customer_id
                    ? 'border-primary-400 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {c.display_name}
                <StatusBadge status={c.status} />
              </button>
            ))}
          </div>
        )}
      </div>

      {!customerId ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <p className="text-sm text-gray-500 font-medium">Select a customer above to view its logs</p>
        </div>
      ) : !isCustomerActive ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <p className="text-sm text-gray-500 font-medium">
            '{selectedCustomer?.display_name ?? customerId}' is not connected
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Job and step history are served by that customer's ETL service, which requires it to be
            connected. Connect it from the Projects tab, then come back here.
          </p>
        </div>
      ) : (
        <>
          {/* Job history */}
          {jobsError ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <p className="text-xs text-red-600">{jobsError}</p>
            </div>
          ) : (
            <ETLJobHistory jobs={jobs} loading={jobsLoading} />
          )}

          {/* Step-run history */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Step Run History</h3>
            </div>
            <div className="px-3 py-2 border-b border-gray-100 flex gap-1.5 flex-wrap bg-gray-50">
              {STEP_DEFS.map(stepDef => (
                <button
                  key={stepDef.id}
                  onClick={() => setSelectedStepId(stepDef.id)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                    selectedStepId === stepDef.id
                      ? `${stepDef.bgSelected} text-white shadow-sm`
                      : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {stepDef.num}. {stepDef.title}
                </button>
              ))}
            </div>
            <div className="p-3">
              {stepHistoryLoading ? (
                <p className="text-xs text-gray-400">Loading…</p>
              ) : stepHistoryError ? (
                <p className="text-xs text-red-600">{stepHistoryError}</p>
              ) : stepHistory.length === 0 ? (
                <p className="text-xs text-gray-400">No runs recorded for this step yet.</p>
              ) : (
                stepHistory.map(run => <StepRunRow key={run.step_run_id} run={run} />)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
